import { HFlow, Icon, PagedTable, Tag, Text, Tooltip } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import { ButtonLink } from 'components/route'
import { TableBox } from 'components/table/TableBox'
import React from 'react'
import { useHistory, useRouteMatch } from 'react-router'

import { Ellipsis } from '../../../components/Ellipsis'
import { usePagedTableProps } from '../../../components/table'
import { useAtividadeColetivaGrupoTableQuery } from '../../../graphql/hooks.generated'
import { GrupoParticipanteAtividadeColetivaQueryInput } from '../../../graphql/types.generated'
import { convertQueryDataToTableData } from '../grupos/converter-atividadeColetivaGrupos'
import { useAtividadeColetivaGruposExcluir } from '../grupos/hooks/useAtividadeColetivaGruposExcluir'
import { useAtividadeColetivaGruposStatusAtivoUpdate } from '../grupos/hooks/useAtividadeColetivaGruposStatusAtivoUpdate'
import {
  AtividadeColetivaGruposFilterFormModel,
  AtividadeColetivaGruposTableModel,
} from '../grupos/model-atividadeColetivaGrupos'
import { AtividadeColetivaGruposTableDropdownMenu } from './AtividadeColetivaGruposTableDropdownMenu'

interface AtividadeColetivaGruposTableProps {
  filter: GrupoParticipanteAtividadeColetivaQueryInput
  onChangeFilter: (filter: AtividadeColetivaGruposFilterFormModel) => void
}

export const AtividadeColetivaGruposTable = (props: AtividadeColetivaGruposTableProps) => {
  const { filter, onChangeFilter } = props
  const match = useRouteMatch()
  const history = useHistory()
  const handleRejection = useErrorHandler()

  const { data, loading, refetch } = useAtividadeColetivaGrupoTableQuery({
    variables: { input: filter },
    fetchPolicy: 'cache-and-network',
  })

  const queryContent = data?.gruposParticipantesAtividadeColetiva?.content
  const convertedQueryPayload = queryContent?.map((grupo) => convertQueryDataToTableData(grupo)) || []
  const tableData = data?.gruposParticipantesAtividadeColetiva && {
    content: convertedQueryPayload,
    pageInfo: data.gruposParticipantesAtividadeColetiva.pageInfo,
  }

  const tableProps = usePagedTableProps({
    loading: loading,
    result: tableData,
    onChange: onChangeFilter,
  })

  const { excluirGrupo } = useAtividadeColetivaGruposExcluir({
    onSuccess: () => {
      refetch().catch(handleRejection)
    },
  })

  const handleEditar = (grupoParticipanteAtividadeColetivaId: ID) => {
    history.push(`${match.url}/editar/${grupoParticipanteAtividadeColetivaId}`)
  }

  const { updateStatusAtivo } = useAtividadeColetivaGruposStatusAtivoUpdate({
    onSuccess: () => {
      refetch({ input: filter })
    },
  })

  const renderActions = (item: AtividadeColetivaGruposTableModel) => (
    <HFlow hSpacing={0} justifyContent='flex-end'>
      <Tooltip text='Visualizar'>
        <ButtonLink size='small' skin='ghost' to={`${match.url}/visualizar/${item.id}`}>
          <Icon icon='zoomOutline' />
        </ButtonLink>
      </Tooltip>
      <AtividadeColetivaGruposTableDropdownMenu
        isGrupoAtivo={item.ativo}
        onUpdateStatusAtivo={() => updateStatusAtivo(item.id, item.ativo)}
        onExcluir={() => excluirGrupo(item.id)}
        onEditar={() => handleEditar(item.id)}
      />
    </HFlow>
  )

  return (
    <TableBox>
      <PagedTable<AtividadeColetivaGruposTableModel>
        loading={loading}
        columns={[
          {
            name: 'nomeGrupo',
            header: 'Nome do grupo',
            render: (row) => <Text>{row.nome}</Text>,
          },
          {
            name: 'tipoGrupo',
            header: 'Tipo de grupo',
            render: (row) => <Text>{row.tipoGrupo}</Text>,
          },
          {
            name: 'quantidadeIntegrantes',
            header: 'Nº de integrantes',
            render: (row) => <Text>{row.quantidadeIntegrantes}</Text>,
          },
          {
            name: 'unidadeSaude',
            header: 'Unidade de saúde',
            render: (row) => <Text>{row.unidadeSaude}</Text>,
          },
          {
            name: 'equipesEnvolvidas',
            header: 'Equipes envolvidas',
            render: (row) => <Ellipsis maxLines={2}>{row.equipesEnvolvidas}</Ellipsis>,
            style: { maxWidth: '16rem' },
          },
          {
            name: 'isAtivo',
            render: (row) => (row.ativo ? '' : <Tag type='normal'>Inativo</Tag>),
          },
          {
            name: 'actions',
            align: 'right',
            render: renderActions,
          },
        ]}
        {...tableProps}
      />
    </TableBox>
  )
}
